import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export interface ForecastGraphProps {
  data: {
    period: string;
    totalQuantity: number;
    totalRevenue: number;
  }[];
  title: string;
  startDate?: Date | null;
  endDate?: Date | null;
}

const getNext12Months = (data: { period: string; totalQuantity: number; totalRevenue: number }[]) => {
  if (!data.length) return [];
  // Sort periods ascending
  const sorted = [...data].sort((a, b) => a.period.localeCompare(b.period));
  // Find the latest period
  const lastIdx = sorted.length - 1;
  // Get the last 12 periods
  return sorted.slice(Math.max(0, lastIdx - 11), lastIdx + 1);
};

const getQuarter = (period: string) => {
  const month = parseInt(period.split('-')[1], 10);
  if (month >= 1 && month <= 3) return 'Q1';
  if (month >= 4 && month <= 6) return 'Q2';
  if (month >= 7 && month <= 9) return 'Q3';
  if (month >= 10 && month <= 12) return 'Q4';
  return '';
};

// Adjust x-axis granularity based on date range
const determineLabels = (data: { period: string; totalQuantity: number; totalRevenue: number }[], startDate: Date | null, endDate: Date | null) => {
  if (!startDate || !endDate) return data.map(item => item.period);
  const diffInDays = (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24);
  if (diffInDays <= 31) {
    // Show days
    return data.map(item => new Date(item.period + '-01').toLocaleDateString('en-US', { day: 'numeric', month: 'short' }));
  } else if (diffInDays <= 90) {
    // Show weeks
    return data.map(item => {
      const date = new Date(item.period + '-01');
      const weekNumber = Math.ceil(date.getDate() / 7);
      return `Week ${weekNumber} of ${date.toLocaleDateString('en-US', { month: 'short' })}`;
    });
  } else {
    // Show months
    return data.map(item => new Date(item.period + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }));
  }
};

export const ForecastGraph: React.FC<ForecastGraphProps> = ({ data, title, startDate, endDate }) => {
  const [showUnits, setShowUnits] = useState(true);
  const [showRevenue, setShowRevenue] = useState(true);
  const [showQuarters, setShowQuarters] = useState(false);

  // Only show next 12 months
  const next12 = useMemo(() => getNext12Months(data), [data]);

  // Chart.js plugin to highlight quarter
  const quarterHighlightPlugin = {
    id: 'quarterHighlight',
    beforeDraw: (chart: any) => {
      if (!showQuarters) return;
      const ctx = chart.ctx;
      const xAxis = chart.scales.x;
      const yAxis = chart.scales.y;
      ctx.save();
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = '#22c55e';
      next12.forEach((item, idx) => {
        const quarter = getQuarter(item.period);
        if (quarter) {
          const x = xAxis.getPixelForValue(idx);
          const nextX = xAxis.getPixelForValue(idx + 1) || xAxis.right;
          ctx.fillRect(x, yAxis.top, nextX - x, yAxis.bottom - yAxis.top);
        }
      });
      ctx.restore();
    }
  };

  const labels = useMemo(() => determineLabels(next12, startDate || null, endDate || null), [next12, startDate, endDate]);

  const chartData = {
    labels,
    datasets: [
      ...(showUnits ? [{
        label: 'Units',
        data: next12.map(item => item.totalQuantity),
        borderColor: 'rgb(0, 109, 121)',
        backgroundColor: 'rgba(0, 109, 121, 0.5)',
        yAxisID: 'y',
      }] : []),
      ...(showRevenue ? [{
        label: 'Revenue ($)',
        data: next12.map(item => item.totalRevenue),
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        yAxisID: 'y1',
      }] : [])
    ]
  };

  const options = {
    responsive: true,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    stacked: false,
    plugins: {
      title: {
        display: true,
        text: title,
      },
      legend: {
        position: 'top' as const,
      }
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: showUnits,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Units'
        }
      },
      y1: {
        type: 'linear' as const,
        display: showRevenue,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Revenue ($)'
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return (
    <div className="forecast-graph">
      <div className="graph-controls" style={{ marginBottom: '1rem' }}>
        <label style={{ marginRight: '1rem' }}>
          <input
            type="checkbox"
            checked={showUnits}
            onChange={(e) => setShowUnits(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Show Units
        </label>
        <label style={{ marginRight: '1rem' }}>
          <input
            type="checkbox"
            checked={showRevenue}
            onChange={(e) => setShowRevenue(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          Show Revenue
        </label>
        <label style={{ marginRight: '1rem' }}>
          <input
            type="checkbox"
            checked={showQuarters}
            onChange={(e) => setShowQuarters(e.target.checked)}
            style={{ marginRight: '0.5rem' }}
          />
          VIEW QUARTERS
        </label>
      </div>
      <div style={{ height: '400px' }}>
        <Line options={options} data={chartData} plugins={[quarterHighlightPlugin]} />
      </div>
    </div>
  );
}; 